import superagent from 'superagent';
import {
  all,
  call,
  put,
  putResolve,
  race,
  take,
  takeLatest,
} from 'redux-saga/effects';
import { AnyAction } from 'redux';
import { success, error } from '@redux-requests/core';
import { toast } from 'react-toastify';
import { isNil } from 'lodash-es';

import { DefaultHeadersObject } from 'shared/utils/request-utils';
import {
  ADD_SAFELIST_MODAL,
  EDIT_SAFELIST_MODAL,
  REMOVE_TERM_MODAL,
  REMOVE_SAFELIST_MODAL,
} from 'shared/constants/modalNames';

import { closeModal, openModal } from '../modal/actions';

import {
  createSafelist,
  patchSafelist,
  fetchSafelistSummaries,
  fetchSafelistByGuid,
  ADD_SAFELIST,
  START_SAFELIST_EDITING,
  FETCH_SAFELIST_BY_GUID,
  EXTRACT_TERMS_FROM_TEXT,
  PATCH_SAFELIST,
  DELETE_SAFELIST,
  DELETE_SAFELIST_TERM,
} from './actions';
import { CreateSafelistAction, DeleteSafelistTermAction } from './types';

// Watcher sagas.
function* watchAddSafelistAction() {
  yield takeLatest(ADD_SAFELIST, handleSafelistCreation);
}

function* watchEditSafelistAction() {
  yield takeLatest(START_SAFELIST_EDITING, handleSafelistEditing);
}

function* watchExtractTermsAction() {
  yield takeLatest(EXTRACT_TERMS_FROM_TEXT, handleTermsExtraction);
}

function* watchTermDeletionAction() {
  yield takeLatest(DELETE_SAFELIST_TERM, handleTermDeletion);
}

function* watchUpdateSafelistAction() {
  yield takeLatest(PATCH_SAFELIST, handleSafelistUpdating);
}

function* watchSafelistDeletionAction() {
  yield takeLatest(DELETE_SAFELIST, handleSafelistDeletion);
}

// Worker sagas.
function* handleSafelistCreation(action: CreateSafelistAction) {
  const { name, terms = [] } = action.payload;

  try {
    const { action, error, ...createdSafelist } = yield putResolve(createSafelist(name!));

    if (!isNil(error)) {
      throw new Error(error);
    }

    if (terms.length) {
      const { error: patchError } = yield putResolve(patchSafelist(createdSafelist.guid, terms));

      if (patchError) {
        throw new Error(patchError);
      }
    }

    yield putResolve(fetchSafelistSummaries());
    yield put({ type: success(ADD_SAFELIST) });
    yield put(closeModal(ADD_SAFELIST_MODAL));

  } catch (e) {
    yield put({ type: error(ADD_SAFELIST) });
    yield call(toast.error, e.message);
  }
}

function* handleSafelistEditing(action: AnyAction) {
  const { safelistGuid } = action.payload;

  yield put(fetchSafelistByGuid(safelistGuid));
  yield put(openModal(EDIT_SAFELIST_MODAL));

  const { failedAction } = yield race({
    successfulAction: take(success(FETCH_SAFELIST_BY_GUID)),
    failedAction: take(error(FETCH_SAFELIST_BY_GUID)),
  });

  if (failedAction) {
    yield put(closeModal(EDIT_SAFELIST_MODAL));
    yield call(toast.error, 'An error occurred while fetching the safelist.');
  }
}

function* handleTermsExtraction() {
  try {
    const { successfulAction, failedAction } = yield race({
      successfulAction: take(success(EXTRACT_TERMS_FROM_TEXT)),
      failedAction: take(error(EXTRACT_TERMS_FROM_TEXT)),
    });

    if (failedAction) {
      throw new Error('An error occurred while extracting terms from the provided text.');
    }

    const { data } = successfulAction.response;

    if (!data.length) {
      throw new Error('No terms found. Please try again with a different text.');
    }

  } catch (e) {
    yield call(toast.error, e.message);
  }
}

function* handleTermDeletion(action: DeleteSafelistTermAction) {
  const { safelistGuid, termGuid } = action.payload;

  try {
    // TODO: Temporary! We had to do the request manually instead of using `redux-requests`
    // because there's a bug that breaks the app when the server returns nothing.
    yield superagent
      .delete(`/services/safelist-libraries/${safelistGuid}/${termGuid}`)
      .set(DefaultHeadersObject.get());

    yield put({
      type: success(DELETE_SAFELIST_TERM),
      payload: {
        safelistGuid,
        termGuid,
      },
    });
    yield put(closeModal(REMOVE_TERM_MODAL));

  } catch (e) {
    yield put({ type: error(DELETE_SAFELIST_TERM) });
    yield call(toast.error, e.message);
  }
}

function* handleSafelistUpdating() {
  const { successfulAction } = yield race({
    successfulAction: take(success(PATCH_SAFELIST)),
    failedAction: take(error(PATCH_SAFELIST)),
  });

  if (successfulAction) {
    yield put(fetchSafelistSummaries());
    yield put(closeModal(EDIT_SAFELIST_MODAL));
  } else {
    yield call(toast.error, 'An error occurred while updating the safelist.');
  }
}

function* handleSafelistDeletion(action: AnyAction) {
  const { safelistGuid } = action.payload;

  try {
    // TODO: Temporary! We had to do the request manually instead of using `redux-requests`
    // because there's a bug that breaks the app when the server returns nothing.
    yield superagent
      .delete(`/services/safelist-libraries/${safelistGuid}`)
      .set(DefaultHeadersObject.get());
    yield put({ type: success(DELETE_SAFELIST) });
    yield put(closeModal(REMOVE_SAFELIST_MODAL));
    yield put(fetchSafelistSummaries());

  } catch (_) {
    yield put({ type: error(DELETE_SAFELIST) });
    yield call(toast.error, 'An error occurred while deleting the safelist.');
  }
}

export default function* SafelistsSagas() {
  yield all([
    watchAddSafelistAction(),
    watchEditSafelistAction(),
    watchExtractTermsAction(),
    watchTermDeletionAction(),
    watchUpdateSafelistAction(),
    watchSafelistDeletionAction(),
  ]);
}
